/**
 * Drafts set aside: a Notice, Event or Poll left part-way when another one starts, a
 * question takes the chat elsewhere, or it's cancelled. "Where did my notice go? Carry
 * on with it", "go back to my dance event", "what happened to my bus route notice?"
 * pick one up again where it stopped. Nothing here is sent anywhere; it's the chat's memory.
 */
import { significantWords } from "../significantWords";
import type { TaskName, TaskRecord } from "./taskConversation";

/** How many drafts are kept aside; the oldest goes first. */
export const PARKED_LIMIT = 3;

/** What a reply does to the open task, as far as setting it aside goes. */
type ReplyShape = { kind: string; dropTask?: true; task?: TaskRecord | null; startedBy?: string };

const inProgress = (record: TaskRecord | null | undefined): record is TaskRecord =>
  Boolean(record && record.phase !== "done");

const sameDraft = (a: TaskRecord, b: TaskRecord) =>
  a.task === b.task && a.startedByText === b.startedByText && a.startedById === b.startedById;

/**
 * The drafts set aside after a reply: the open one is set aside when the reply drops it, cancels
 * it, or starts another; a draft picked up again (or the same one carried on) leaves the list.
 */
export function parkedAfter(parked: TaskRecord[], open: TaskRecord | null, reply: ReplyShape): TaskRecord[] {
  const next = reply.kind === "task" ? reply.task ?? null : null;
  const resumed = next ? parked.filter((record) => !sameDraft(record, next)) : parked;
  const left =
    inProgress(open) &&
    (reply.dropTask === true ||
      reply.kind === "cancelled" ||
      (reply.kind === "task" && next === null) ||
      (reply.kind === "task" && next !== null && Boolean(reply.startedBy) && !sameDraft(open, next)));
  if (!left) return resumed;
  return [open, ...resumed.filter((record) => !sameDraft(record, open))].slice(0, PARKED_LIMIT);
}

/**
 * "carry on with it", "go back to my dance event", "where did my notice go?", "what happened to my
 * bus route notice?", "did you keep the science fair event?", "what was I meant to do next?":
 * asking for a draft set aside.
 */
const ASKS_FOR_A_DRAFT = new RegExp(
  [
    "\\b(?:go|get|come)\\s+back\\s+to\\b",
    "\\bback\\s+to\\s+(?:my|the|that|our)\\b",
    "\\bcarry\\s+on\\s+with\\b",
    "\\bcontinue\\s+(?:with\\s+)?(?:my|the|that|it)\\b",
    "\\bresume\\b",
    "\\bfinish\\s+(?:my|the|our)\\s+(?:notice|poll|event|draft)\\b",
    "\\bwhere(?:['’]s|\\s+is)\\s+my\\s+(?:notice|poll|event|draft)\\b[^.?!]*[?!.]\\s*carry\\s+on\\b",
    "\\bpick\\s+(?:it\\s+)?up\\s+(?:again|where)\\b",
    "\\bwhere\\s+did\\s+my\\s+(?:[\\w'-]+\\s+){0,3}?(?:notice|event|poll|draft)\\s+go\\b",
    "\\bwhat\\s+happened\\s+to\\s+(?:my|the)\\b",
    "\\bdid\\s+you\\s+keep\\s+(?:my|the)\\b",
    "\\bwhat\\s+was\\s+i\\s+(?:meant|supposed|going)\\s+to\\s+do\\b",
    "\\bwhat\\s+(?:should|do)\\s+i\\s+do\\s+next\\b",
  ].join("|"),
  "i"
);

export const asksForADraft = (said: string) => ASKS_FOR_A_DRAFT.test(said);

/** The words that ask for a draft, which say nothing about which one: "go back to", "carry on with". */
const ASKING_WORDS = significantWords("go get come back carry on continue resume pick up where did what happened keep meant supposed going do next one that it notice event poll draft");

const KIND_WORDS: Record<TaskName, RegExp> = {
  notice: /\bnotices?\b/i,
  event: /\bevents?\b/i,
  poll: /\bpolls?\b/i,
};

/** Words a draft is known by: its title or question. */
const nameOf = (record: TaskRecord) => {
  const details = record.details as { title?: unknown; question?: unknown };
  return typeof details.title === "string" ? details.title : typeof details.question === "string" ? details.question : "";
};

/**
 * The draft asked for: the one whose title shares the most words with what was said ("my bus route
 * notice"), then the newest of the kind named ("my dance event"), then the newest of all.
 */
export function draftAskedFor(parked: TaskRecord[], asked: string): TaskRecord | undefined {
  if (parked.length === 0) return undefined;
  // "poll done. did you keep the science fair event?": the one named after the asking words
  const at = asked.search(ASKS_FOR_A_DRAFT);
  const said = at > 0 ? asked.slice(at) : asked;
  const words = new Set(Array.from(significantWords(said)).filter((word) => !ASKING_WORDS.has(word)));
  const scored = parked.map((record) => ({
    record,
    shared: Array.from(significantWords(nameOf(record))).filter((word) => words.has(word)).length,
  }));
  const best = scored.reduce((top, row) => (row.shared > top.shared ? row : top), { record: parked[0], shared: 0 });
  if (best.shared > 0) return best.record;
  const named = (Object.keys(KIND_WORDS) as TaskName[]).filter((kind) => KIND_WORDS[kind].test(said));
  if (named.length > 0) return parked.find((record) => named.includes(record.task));
  return parked[0];
}

/** "your Event about Science Fair", said when it's picked up again. */
export function draftName(record: TaskRecord): string {
  const label = record.task === "poll" ? "Poll" : record.task === "event" ? "Event" : "Notice";
  const name = nameOf(record).replace(/[?!.]+$/, "");
  return name ? `your ${label} about ${name}` : `your ${label}`;
}
